import React, { Component } from "react";
import { api, fmt } from "../utils/api";
import toast from "react-hot-toast";
import {
  CheckCircle,
  FileText,
  Trash2,
  RefreshCw,
  Download,
  TrendingUp,
  TrendingDown,
  Calendar,
  Hotel,
  Users,
  IndianRupee,
  Send,
  X,
  Mail,
  AlertTriangle,
  Edit2,
  CreditCard,
  Receipt,
  BookOpen,
  Banknote,
} from "lucide-react";

function formatDate(d) {
  return d ? String(d).slice(0, 10) : "—";
}

function formatDateLong(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return String(d).slice(0, 10);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
}

function StatCard({ icon: Icon, label, value, sub, color, accent }) {
  return (
    <div style={{ background: "#FFFFFF", border: `1px solid ${accent ? "#F5CAC5" : "#E5E7EB"}`, borderRadius: 16, padding: "18px 20px", flex: 1, minWidth: 160, position: "relative", overflow: "hidden", boxShadow: accent ? "0 2px 12px rgba(225,72,58,0.08)" : "0 1px 4px rgba(0,0,0,0.04)" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: color, borderRadius: "16px 16px 0 0" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#9CA3AF" }}>{label}</span>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon size={17} color={color} />
        </div>
      </div>
      <div style={{ fontSize: 21, fontWeight: 800, color: accent ? "#E1483A" : "#111827", letterSpacing: "-0.02em", fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: accent ? "#E1483A" : "#9CA3AF", marginTop: 5, fontWeight: 500 }}>{sub}</div>}
    </div>
  );
}

class BookingsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bookings: [], loading: true, downloading: false, downloadingInv: null,
      emailModal: null, emailTo: "", emailCc: "", emailSending: false,
      paymentModal: null, paymentTxnId: "", paymentMode: "UPI",
      paymentType: "full", paymentAmount: "", paymentNotes: "", paymentSaving: false,
      hotelEmailModal: null, hotelEmailTo: "", hotelEmailSending: false,
      hotelModalTab: "confirm", manualConfNo: "", manualConfirming: false,
      manualRejecting: false, showRejectBox: false, manualRejectReason: "",
      approvalModal: null, approvalReason: "", approving: false,
      rejectionModal: null, rejectionReason: "", rejecting: false,
      cancelModal: null, cancelReason: "", cancelling: false,
      filters: { status: "", mode: "" }, countdown: 60,
      // ── Instant Data Addition ─────────────────────────────────────────────
      quickEditModal: null,  // booking object
      quickEditData:  {},    // fields being edited
      quickEditSaving: false,
    };
    this._timer = null;
    this._interval = null;
    this.fetchBookings        = this.fetchBookings.bind(this);
    this.handleDownload       = this.handleDownload.bind(this);
    this.handleDownloadInvoice= this.handleDownloadInvoice.bind(this);
    this.handleDownloadVoucher= this.handleDownloadVoucher.bind(this);
    this.handleSendEmail      = this.handleSendEmail.bind(this);
    this.handleSendHotelEmail = this.handleSendHotelEmail.bind(this);
    this.handlePaymentSave    = this.handlePaymentSave.bind(this);
    this.handleManualConfirm  = this.handleManualConfirm.bind(this);
    this.handleManualReject   = this.handleManualReject.bind(this);
    this.handleApprove        = this.handleApprove.bind(this);
    this.handleApproveSubmit  = this.handleApproveSubmit.bind(this);
    this.handleReject         = this.handleReject.bind(this);
    this.handleRejectSubmit   = this.handleRejectSubmit.bind(this);
    this.handleQuickEditSave  = this.handleQuickEditSave.bind(this);
    this.handleInvoice        = this.handleInvoice.bind(this);
    this.handleDelete         = this.handleDelete.bind(this);
    this.handleCancel         = this.handleCancel.bind(this);
    this.handleCancelSubmit   = this.handleCancelSubmit.bind(this);
    this.handleFilterChange   = this.handleFilterChange.bind(this);
  }

  componentDidMount()  { this.fetchBookings(); this.startCountdown(); }
  componentWillUnmount() { if (this._interval) clearInterval(this._interval); }
  componentDidUpdate(_, prev) { if (prev.filters !== this.state.filters) this.fetchBookings(); }

  startCountdown() {
    if (this._interval) clearInterval(this._interval);
    this.setState({ countdown: 60 });
    this._interval = setInterval(() => {
      this.setState((prev) => {
        const next = prev.countdown - 1;
        if (next <= 0) { this.fetchBookings(); setTimeout(() => this.startCountdown(), 0); return { countdown: 60 }; }
        return { countdown: next };
      });
    }, 1000);
  }

  async fetchBookings() {
    this.setState({ loading: true });
    try {
      const params = Object.fromEntries(Object.entries(this.state.filters).filter(([, v]) => v));
      const { data } = await api.get("/bookings", { params });
      this.setState({ bookings: data.data || [] });
    } catch { toast.error("Failed to load bookings"); }
    finally  { this.setState({ loading: false }); }
  }

  handleFilterChange(e) {
    const { name, value } = e.target;
    this.setState((prev) => ({ filters: { ...prev.filters, [name]: value } }));
  }

  async handleDownload() {
    this.setState({ downloading: true });
    try {
      const { filters } = this.state;
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.mode)   params.append("mode", filters.mode);
      const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const token    = localStorage.getItem("kts_token");
      const response = await fetch(`${API_BASE}/report/download?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error((await response.json()).message || "Download failed");
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `KTS_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "Download failed"); }
    finally { this.setState({ downloading: false }); }
  }

  async handleDownloadVoucher(id, voucherNo) {
    this.setState({ downloadingInv: id });
    try {
      const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const token    = localStorage.getItem("kts_token");
      const response = await fetch(`${API_BASE}/bookings/${id}/voucher/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || "Download failed");
      const blob = await response.blob(); const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${voucherNo || "Voucher"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "Failed to download voucher"); }
    finally { this.setState({ downloadingInv: null }); }
  }

  async handleDownloadInvoice(id, invoiceNo) {
    this.setState({ downloadingInv: id });
    try {
      const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const token    = localStorage.getItem("kts_token");
      const response = await fetch(`${API_BASE}/bookings/${id}/invoice/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).message || "Download failed");
      const blob = await response.blob(); const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${invoiceNo || "Invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(err.message || "Failed to download invoice"); }
    finally { this.setState({ downloadingInv: null }); }
  }

  async handlePaymentSave() {
    const { paymentModal, paymentTxnId, paymentMode, paymentType, paymentAmount, paymentNotes } = this.state;
    if (!paymentTxnId.trim()) { toast.error("Transaction ID is required"); return; }
    if (!paymentMode)          { toast.error("Select a payment mode"); return; }
    const totalSale  = parseFloat(paymentModal.total_sale) || 0;
    const amountPaid = paymentType === "full" ? totalSale : parseFloat(paymentAmount) || 0;
    if (paymentType === "part" && (!amountPaid || amountPaid <= 0)) { toast.error("Enter a valid partial amount"); return; }
    this.setState({ paymentSaving: true });
    try {
      await api.post(`/bookings/${paymentModal.id}/payment`, {
        transaction_id: paymentTxnId.trim(), payment_mode: paymentMode,
        payment_type: paymentType, amount: amountPaid, notes: paymentNotes.trim() || null,
      });
      toast.success(paymentType === "full" ? "Full payment recorded!" : `Part payment of ₹${amountPaid.toFixed(2)} recorded!`);
      this.setState({ paymentModal: null, paymentTxnId: "", paymentMode: "UPI", paymentType: "full", paymentAmount: "", paymentNotes: "" });
      this.fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to record payment"); }
    finally { this.setState({ paymentSaving: false }); }
  }

  async handleSendHotelEmail() {
    const { hotelEmailModal, hotelEmailTo } = this.state;
    this.setState({ hotelEmailSending: true });
    try {
      const res = await api.post(`/bookings/${hotelEmailModal.id}/send-hotel-email`, { hotel_email: hotelEmailTo.trim() || undefined });
      toast.success(res.data?.message || "Hotel email sent!");
      this.setState({ hotelEmailModal: null, hotelEmailTo: "", hotelModalTab: "confirm" });
      this.fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to send hotel email"); }
    finally { this.setState({ hotelEmailSending: false }); }
  }

  async handleManualConfirm() {
    const { hotelEmailModal, manualConfNo } = this.state;
    if (!manualConfNo.trim()) { toast.error("Enter a confirmation number"); return; }
    this.setState({ manualConfirming: true });
    try {
      const API  = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const res  = await fetch(`${API}/confirm/${hotelEmailModal.confirmation_token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ conf_no: manualConfNo.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Confirmation failed");
      toast.success("Booking confirmed successfully!");
      this.setState({ hotelEmailModal: null, manualConfNo: "", hotelModalTab: "confirm", showRejectBox: false });
      this.fetchBookings();
    } catch (err) { toast.error(err.message || "Failed to confirm booking"); }
    finally { this.setState({ manualConfirming: false }); }
  }

  async handleManualReject() {
    const { hotelEmailModal, manualRejectReason } = this.state;
    if (!manualRejectReason.trim()) { toast.error("Enter a rejection reason"); return; }
    this.setState({ manualRejecting: true });
    try {
      const API  = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const res  = await fetch(`${API}/confirm/${hotelEmailModal.confirmation_token}/reject`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ reason: manualRejectReason.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Rejection failed");
      toast.success("Booking marked as rejected by hotel.");
      this.setState({ hotelEmailModal: null, manualRejectReason: "", hotelModalTab: "confirm", showRejectBox: false });
      this.fetchBookings();
    } catch (err) { toast.error(err.message || "Failed to reject booking"); }
    finally { this.setState({ manualRejecting: false }); }
  }

  async handleSendEmail() {
    const { emailModal, emailTo, emailCc } = this.state;
    if (!emailTo.trim()) { toast.error("Please enter recipient email"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo.trim())) { toast.error("Invalid email address"); return; }
    this.setState({ emailSending: true });
    try {
      const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
      const token    = localStorage.getItem("kts_token");
      const res  = await fetch(`${API_BASE}/bookings/${emailModal.id}/email-invoice`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ to: emailTo.trim(), cc: emailCc.trim() || undefined }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Email failed");
      toast.success(data.message || "Invoice emailed successfully!");
      this.setState({ emailModal: null, emailTo: "", emailCc: "" });
    } catch (err) { toast.error(err.message || "Failed to send email"); }
    finally { this.setState({ emailSending: false }); }
  }

  handleApprove(booking) { this.setState({ approvalModal: booking, approvalReason: "" }); }

  async handleApproveSubmit() {
    const { approvalModal, approvalReason } = this.state;
    if (!approvalReason.trim()) { toast.error("Please enter an approval reason"); return; }
    this.setState({ approving: true });
    try {
      await api.patch(`/bookings/${approvalModal.id}/approve`, { approved_by: "Accounts Manager", approval_reason: approvalReason.trim() });
      toast.success("Booking approved!");
      this.setState({ approvalModal: null, approvalReason: "" });
      this.fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || "Approval failed"); }
    finally { this.setState({ approving: false }); }
  }

  handleReject(booking) { this.setState({ rejectionModal: booking, rejectionReason: "" }); }

  async handleRejectSubmit() {
    const { rejectionModal, rejectionReason } = this.state;
    if (!rejectionReason.trim()) { toast.error("Please enter a rejection reason"); return; }
    this.setState({ rejecting: true });
    try {
      await api.patch(`/bookings/${rejectionModal.id}/reject`, { rejection_reason: rejectionReason.trim() });
      toast.success("Booking rejected. Client has been notified.");
      this.setState({ rejectionModal: null, rejectionReason: "" });
      this.fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || "Rejection failed"); }
    finally { this.setState({ rejecting: false }); }
  }

  handleCancel(booking) { this.setState({ cancelModal: booking, cancelReason: "" }); }

  async handleCancelSubmit() {
    const { cancelModal, cancelReason } = this.state;
    if (!cancelReason.trim()) { toast.error("Please enter a cancellation reason"); return; }
    this.setState({ cancelling: true });
    try {
      await api.patch(`/bookings/${cancelModal.id}/cancel`, { cancellation_reason: cancelReason.trim() });
      toast.success("Booking cancelled. Hotel and client have been notified.");
      this.setState({ cancelModal: null, cancelReason: "" });
      this.fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || "Cancellation failed"); }
    finally { this.setState({ cancelling: false }); }
  }

  // ── All editable fields for a booking ────────────────────────────────────────
  getAllEditableFields(b) {
    return [
      // Guest Info
      { key: 'guest_name',          label: '👤 Guest Name',        type: 'text',   section: 'Guest' },
      { key: 'guest_phone',         label: '📞 Guest Phone',       type: 'tel',    section: 'Guest' },
      { key: 'client_email',        label: '📧 Client Email',      type: 'email',  section: 'Guest' },
      { key: 'sold_to',             label: '🏢 Sold To',           type: 'text',   section: 'Guest' },
      { key: 'billing_state_sale',  label: '🗺️ Billing State',     type: 'text',   section: 'Guest' },
      // Booking Info
      { key: 'conf_no',             label: '🔖 Conf. Number',      type: 'text',   section: 'Booking' },
      { key: 'booking_date',        label: '📅 Booking Date',      type: 'date',   section: 'Booking' },
      { key: 'booked_by',           label: '🧑‍💼 Booked By',         type: 'text',   section: 'Booking' },
      { key: 'sale_type',           label: '📦 Sale Type',         type: 'text',   section: 'Booking' },
      // Hotel & Stay
      { key: 'hotel_name',          label: '🏨 Hotel Name',        type: 'text',   section: 'Hotel' },
      { key: 'destination',         label: '📍 Destination',       type: 'text',   section: 'Hotel' },
      { key: 'check_in',            label: '📅 Check In',          type: 'date',   section: 'Hotel' },
      { key: 'check_out',           label: '📅 Check Out',         type: 'date',   section: 'Hotel' },
      { key: 'no_of_nights',        label: '🌙 No. of Nights',     type: 'number', section: 'Hotel' },
      // Financial
      { key: 'total_sale',          label: '💰 Total Sale (₹)',    type: 'number', section: 'Financial' },
      { key: 'total_purchase',      label: '🛒 Total Purchase (₹)',type: 'number', section: 'Financial' },
      { key: 'extras_amount_sale',  label: '➕ Extras Sale (₹)',   type: 'number', section: 'Financial' },
      { key: 'extras_details_sale', label: '📝 Extras Details',    type: 'text',   section: 'Financial' },
      // Notes
      { key: 'negative_reason',     label: '⚠️ Negative Reason',   type: 'text',   section: 'Notes' },
      { key: 'hotel_email_to',      label: '📧 Hotel Email To',    type: 'email',  section: 'Notes' },
      { key: 'hotel_email_cc',      label: '📧 Hotel Email CC',    type: 'email',  section: 'Notes' },
    ];
  }

  // ── Detect only missing fields ────────────────────────────────────────────────
  getMissingFields(b) {
    const required = [
      { key: 'guest_name',          label: '👤 Guest Name'     },
      { key: 'guest_phone',         label: '📞 Guest Phone'    },
      { key: 'client_email',        label: '📧 Client Email'   },
      { key: 'sold_to',             label: '🏢 Sold To'        },
      { key: 'check_in',            label: '📅 Check In'       },
      { key: 'check_out',           label: '📅 Check Out'      },
      { key: 'conf_no',             label: '🔖 Conf. Number'   },
      { key: 'billing_state_sale',  label: '🗺️ Billing State'  },
    ];
    return required.filter(f => !b[f.key]?.toString().trim());
  }

  // ── Save quick-edit fields (non-blocking, subject to admin approval) ──────────
  async handleQuickEditSave() {
    const { quickEditModal, quickEditData } = this.state;
    if (!quickEditModal) return;
    this.setState({ quickEditSaving: true });
    try {
      // Send full booking merged with edits — room_type_bookings excluded to preserve existing rows
      const { room_type_bookings, ...rest } = quickEditData;
      await api.put(`/bookings/${quickEditModal.id}`, rest);
      toast.success('Booking updated successfully!');
      this.setState({ quickEditModal: null, quickEditData: {} });
      this.fetchBookings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      this.setState({ quickEditSaving: false });
    }
  }

  async handleInvoice(id) {
    if (!window.confirm("Mark as Invoiced and generate PDF?")) return;
    try {
      const res = await api.patch(`/bookings/${id}/invoice`);
      toast.success(res.data?.message || "Invoiced & PDF generated!");
      this.fetchBookings();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  }

  async handleDelete(id) {
    if (!window.confirm("Delete this booking?")) return;
    try { await api.delete(`/bookings/${id}`); toast.success("Deleted"); this.fetchBookings(); }
    catch (err) { toast.error(err.response?.data?.message || "Cannot delete"); }
  }

  getStatusBadge(status) {
    const map = {
      Draft:              { cls: "badge badge-draft"    },
      "Pending Approval": { cls: "badge badge-pending"  },
      Approved:           { cls: "badge badge-approved" },
      Invoiced:           { cls: "badge badge-invoiced" },
      Rejected:           { cls: "badge badge-rejected" },
      Cancelled:          { cls: "badge badge-rejected" },
    };
    return map[status] || { cls: "badge badge-draft" };
  }

  render() {
    const { bookings, loading, filters, downloading, downloadingInv, approvalModal, approving, rejectionModal, rejecting, cancelModal, cancelling, countdown } = this.state;

    // ── Live GP helper — mode-aware: Full Input = ex-GST (TAC conditional),
    // Service Charge = GST-inclusive (Total already nets TAC) ──
    const computeLiveGP = (b) => {
      if (b.status === "Cancelled") return 0;
      const rtb    = b.room_type_bookings || [];
      const nights = parseInt(b.total_nights || b.no_of_nights) || 1;
      if (rtb.length === 0) return parseFloat(b.gross_profit) || 0;
      const isSC = b.mode === "Service Charge";
      let saleBase = 0, purBase = 0, tacTotal = 0, saleGst = 0, purGst = 0;
      for (const r of rtb) {
        const qty       = parseInt(r.room_count || r.count) || 0;
        const tacRoom   = parseFloat(r.tac_on_room)   || 0;
        const tacEB     = parseFloat(r.tac_on_ex_bed)  || 0;
        const ebc       = parseInt(r.extra_bed_child)  || 0;
        const eba       = parseInt(r.extra_bed_adult)  || 0;
        const roomSale  = qty * nights * (parseFloat(r.sale_price)||0);
        const roomPur   = qty * nights * (parseFloat(r.purchase_price)||0);   // gross
        const ebcPur    = ebc * nights * (parseFloat(r.ex_bed_rate_child_purchase)||0);
        const ebaPur    = eba * nights * (parseFloat(r.ex_bed_rate_purchase)||0);  // gross
        const ebSale    = ebc * nights * (parseFloat(r.ex_bed_rate_child_sale)||0)
                         + eba * nights * (parseFloat(r.ex_bed_rate_sale)||0);
        saleBase += roomSale + ebSale + (parseFloat(r.extras_amount_sale)||0);
        purBase  += roomPur  + ebcPur + ebaPur + (parseFloat(r.extras_amount_purchase)||0);
        tacTotal += tacRoom + tacEB;  // flat, single — not multiplied by qty/nights
        saleGst  += (roomSale + ebSale) * ((parseFloat(r.gst_rate) || 0) / 100);
        purGst   += (roomPur + ebcPur + ebaPur) * ((parseFloat(r.purchase_gst_rate) || 0) / 100);
      }
      if (isSC) {
        const saleTotal = saleBase + saleGst;
        const purTotal  = Math.max(0, purBase + purGst - tacTotal);
        return Math.round((saleTotal - purTotal) * 100) / 100;
      }
      const gpBase = saleBase - purBase;
      return Math.round((gpBase < 0 ? gpBase + tacTotal : gpBase - tacTotal) * 100) / 100;
    };

    const totalSale     = bookings.reduce((s, b) => s + (parseFloat(b.total_sale)     || 0), 0);
    const totalPurchase = bookings.reduce((s, b) => s + (parseFloat(b.total_purchase) || 0), 0);
    const liveGPs       = bookings.map(b => computeLiveGP(b));
    const totalGP       = liveGPs.reduce((s, gp) => s + gp, 0);
    const invoicedCount = bookings.filter(b => b.status === "Invoiced").length;
    const lossBookings  = bookings.filter((b, i) => liveGPs[i] < 0);
    const lossCount     = lossBookings.length;
    const totalLoss     = lossBookings.reduce((s, b, i) => {
      const idx = bookings.indexOf(b);
      return s + liveGPs[idx];
    }, 0);
    const profitBookings= bookings.filter((b, i) => liveGPs[i] >= 0);
    const totalProfit   = profitBookings.reduce((s, b) => {
      const idx = bookings.indexOf(b);
      return s + liveGPs[idx];
    }, 0);

    return (
      <div>
        {/* ── Stat cards ── */}
        <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
          <StatCard icon={Hotel}       label="Total Bookings" value={bookings.length}                                     sub={`${invoicedCount} invoiced`}                                        color="#2F5FE0" />
          <StatCard icon={IndianRupee} label="Total Sale"     value={`₹${fmt(totalSale)}`}                               sub="across all bookings"                                                color="#0FA968" />
          <StatCard icon={TrendingUp}  label="Net Profit"     value={`₹${fmt(totalProfit)}`}                             sub={`${profitBookings.length} profitable bookings`}                     color="#0FA968" />
          <StatCard icon={TrendingDown}label="Total Loss"     value={lossCount > 0 ? `₹${fmt(Math.abs(totalLoss))}` : "₹0.00"} sub={lossCount > 0 ? `${lossCount} booking${lossCount > 1 ? "s" : ""} in loss` : "No loss bookings"} color="#E1483A" accent={lossCount > 0} />
          <StatCard icon={IndianRupee} label="Gross Profit"   value={`₹${fmt(totalGP)}`}                                sub={totalGP >= 0 ? "Overall profitable" : "Overall in loss"}           color={totalGP >= 0 ? "#0FA968" : "#E1483A"} accent={totalGP < 0} />
        </div>

        {/* ── Filters bar ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <select name="status" className="filter-select" value={filters.status} onChange={this.handleFilterChange}>
              <option value="">All Statuses</option>
              {["Draft", "Pending Approval", "Approved", "Invoiced", "Cancelled"].map(s => <option key={s}>{s}</option>)}
            </select>
            <select name="mode" className="filter-select" value={filters.mode} onChange={this.handleFilterChange}>
              <option value="">All Modes</option>
              <option>Full Input</option>
              <option>Service Charge</option>
            </select>
            <button onClick={() => { this.fetchBookings(); this.startCountdown(); }} className="btn btn-ghost" style={{ padding: "8px 14px", fontSize: 13 }}>
              <RefreshCw size={13} /> Refresh
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 10 }}>
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: countdown <= 10 ? "#E1483A" : countdown <= 30 ? "#B7791F" : "#0FA968", animation: countdown <= 10 ? "pulse 1s infinite" : "none", flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: "#0369A1", fontWeight: 600, whiteSpace: "nowrap" }}>Refresh in {countdown}s</span>
            </div>
          </div>
          <button onClick={this.handleDownload} disabled={downloading} className="btn btn-gold">
            {downloading ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Generating…</> : <><Download size={13} /> Download Report</>}
          </button>
        </div>

        {/* ── Table ── */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 18, overflow: "hidden" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#6B7280" }}>
              <RefreshCw size={24} style={{ animation: "spin 1s linear infinite", marginBottom: 12 }} />
              <p style={{ fontSize: 14 }}>Loading bookings…</p>
            </div>
          ) : bookings.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#6B7280" }}>
              <Calendar size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>No bookings found</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>Try adjusting your filters</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="kts-table">
                <thead>
                  <tr>{["#", "Date", "Guest", "Hotel", "Sold To", "Conf No", "Mode", "Sale", "Purchase", "TAC", "Gross Profit", "Follow Up", "Status", "Actions"].map(h => <th key={h}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {bookings.map((b) => {
                    const gp = computeLiveGP(b);
                    const { cls } = this.getStatusBadge(b.status);
                    return (
                      <tr key={b.id}>
                        <td><span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 11, color: "#6A7A99", fontWeight: 500 }}>#{b.id}</span></td>
                        <td style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(b.booking_date)}</td>
                        <td><div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>{b.guest_name}</div></td>
                        <td style={{ color: "#A8B4CC" }}>{b.hotel_name}</td>
                        <td style={{ color: "#A8B4CC" }}>{b.sold_to}</td>
                        <td style={{ padding: "13px 16px" }}>
                          {b.hotel_status === "Rejected" ? (
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#B5382D", background: "#FCEAE8", padding: "3px 8px", borderRadius: 6, border: "1px solid #F5CAC5", whiteSpace: "nowrap" }} title={b.rejection_reason}>✗ Rejected</span>
                          ) : b.conf_no ? (
                            <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 11, fontWeight: 700, color: "#0B7A4C", background: "#E4F9EE", padding: "3px 8px", borderRadius: 6, border: "1px solid #BEEED9", whiteSpace: "nowrap" }}>✓ {b.conf_no}</span>
                          ) : (
                            <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <span style={{ fontSize: 11, color: "#B7791F", background: "#FFFBEB", padding: "3px 8px", borderRadius: 6, border: "1px solid #FDE68A", whiteSpace: "nowrap" }}>⏳ Awaiting</span>
                              <button title="Enter confirmation / reject" onClick={() => this.setState({ hotelEmailModal: b, manualConfNo: "", showRejectBox: false, manualRejectReason: "" })} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 5, cursor: "pointer", padding: 0, color: "#2F5FE0", flexShrink: 0 }}>
                                <Edit2 size={11} />
                              </button>
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={b.mode === "Full Input" ? "badge badge-full" : "badge badge-sc"}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
                            {b.mode === "Full Input" ? "Full" : "SC"}
                          </span>
                        </td>
                        <td><span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>₹{fmt(b.total_sale)}</span></td>
                        <td><span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 13, color: "#A8B4CC", whiteSpace: "nowrap" }}>₹{fmt(b.total_purchase)}</span></td>
                        <td>{(() => {
                          const rtb = b.room_type_bookings || [];
                          const nights = parseInt(b.total_nights || b.no_of_nights) || 1;
                          const tac = rtb.reduce((s, r) => {
                            const qty = parseInt(r.room_count || r.count) || 0;
                            const ebaCount = parseInt(r.extra_bed_adult) || 0;
                            return s
                              + (parseFloat(r.tac_on_room)   || 0) * qty * nights
                              + (parseFloat(r.tac_on_ex_bed) || 0) * ebaCount * nights;
                          }, 0);
                          return tac > 0
                            ? <span style={{ fontFamily:"JetBrains Mono,monospace", fontSize:12, fontWeight:700, color:"#0FA968", whiteSpace:"nowrap" }}>-₹{fmt(tac)}</span>
                            : <span style={{ color:"#D1D5DB" }}>—</span>;
                        })()}</td>
                        <td>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {gp >= 0 ? <TrendingUp size={13} color="#0FA968" /> : <TrendingDown size={13} color="#E1483A" />}
                            <span style={{ fontFamily: "JetBrains Mono,monospace", fontSize: 13, fontWeight: 700, color: gp >= 0 ? "#0B7A4C" : "#B5382D", whiteSpace: "nowrap" }}>₹{fmt(gp)}</span>
                          </div>
                        </td>
                        {/* Follow Up */}
                        <td>
                          {b.follow_up === "Yes" ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#0FA968", background: "#E4F9EE", border: "1px solid #BEEED9", borderRadius: 20, padding: "2px 9px", whiteSpace: "nowrap" }}>
                                ✓ Yes
                              </span>
                              {b.follow_up_date && (
                                <span style={{ fontSize: 10, fontWeight: 600, color: "#0369A1", background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 20, padding: "2px 9px", whiteSpace: "nowrap" }}>
                                  📅 {new Date(b.follow_up_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span style={{ fontSize: 11, color: "#9CA3AF" }}>—</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
                            <span className={cls}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block", flexShrink: 0 }} />{b.status}</span>
                            {b.payment_transaction_id && (() => {
                              const paid = parseFloat(b.payment_amount) || 0;
                              const total = parseFloat(b.total_sale) || 0;
                              const isPartial = paid < total;
                              return (
                                <span title={`Txn: ${b.payment_transaction_id} | Paid: ₹${paid.toFixed(2)}`} style={{ fontSize: 10, fontWeight: 700, color: isPartial ? "#92400E" : "#0B7A4C", background: isPartial ? "#FFFBEB" : "#E4F9EE", border: `1px solid ${isPartial ? "#FDE68A" : "#BEEED9"}`, borderRadius: 10, padding: "2px 7px", whiteSpace: "nowrap" }}>
                                  {isPartial ? "₹ Part Paid" : "₹ Paid"}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            {b.status === "Pending Approval" && this.props.user?.role_name === "Admin" && (
                              <>
                                <button title="Approve (Admin only)" onClick={() => this.handleApprove(b)} className="icon-btn" style={{ color: "#B7791F", border: "1px solid rgba(245,158,11,0.25)", background: "rgba(245,158,11,0.08)" }}><CheckCircle size={14} /></button>
                                <button title="Reject (Admin only)"  onClick={() => this.handleReject(b)}  className="icon-btn" style={{ color: "#E1483A", border: "1px solid rgba(225,72,58,0.25)", background: "rgba(225,72,58,0.08)" }}><X size={14} /></button>
                              </>
                            )}
                            {(b.status === "Draft" || b.status === "Approved") && ["Admin", "Manager", "Accounts","Staff"].includes(this.props.user?.role_name) && (
                              <button title="Mark as Invoiced & Generate PDF" onClick={() => this.handleInvoice(b.id)} className="icon-btn" style={{ color: "#6EE7B7", border: "1px solid rgba(16,185,129,0.25)", background: "rgba(16,185,129,0.08)" }}><Receipt size={14} /></button>
                            )}
                            {b.status === "Invoiced" && b.voucher_no && (
                              <button title={`Download Service Voucher ${b.voucher_no}`} onClick={() => this.handleDownloadVoucher(b.id, b.voucher_no)} disabled={downloadingInv === b.id} className="icon-btn" style={{ color: "#A78BFA", border: "1px solid rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.08)" }}><BookOpen size={14} /></button>
                            )}
                            {(b.status === "Approved" || b.status === "Invoiced") && (() => {
                              const totalSale    = parseFloat(b.total_sale)    || 0;
                              const paidAmount   = parseFloat(b.payment_amount)|| 0;
                              const fullyPaid    = b.payment_transaction_id && paidAmount >= totalSale;
                              const partiallyPaid= b.payment_transaction_id && paidAmount < totalSale;
                              const balance      = Math.max(0, totalSale - paidAmount);
                              return (
                                <button
                                  title={fullyPaid ? `Fully paid: ₹${paidAmount.toFixed(2)}` : partiallyPaid ? `Part paid: ₹${paidAmount.toFixed(2)} — Balance: ₹${balance.toFixed(2)}` : "Record Payment"}
                                  onClick={() => { if (fullyPaid) return; this.setState({ paymentModal: b, paymentTxnId: "", paymentMode: "UPI", paymentType: partiallyPaid ? "part" : "full", paymentAmount: partiallyPaid ? String(balance) : String(totalSale), paymentNotes: "" }); }}
                                  className="icon-btn" disabled={fullyPaid}
                                  style={{ color: fullyPaid ? "#9CA3AF" : partiallyPaid ? "#D97706" : "#0FA968", border: fullyPaid ? "1px solid rgba(156,163,175,0.3)" : partiallyPaid ? "1px solid rgba(217,119,6,0.35)" : "1px solid rgba(5,150,105,0.25)", background: fullyPaid ? "rgba(156,163,175,0.06)" : partiallyPaid ? "rgba(217,119,6,0.08)" : "rgba(5,150,105,0.08)", cursor: fullyPaid ? "not-allowed" : "pointer", opacity: fullyPaid ? 0.5 : 1, position: "relative" }}>
                                  <Banknote size={14} />
                                  {partiallyPaid && <span style={{ position: "absolute", top: -3, right: -3, width: 8, height: 8, borderRadius: "50%", background: "#B7791F", border: "2px solid #fff" }} />}
                                </button>
                              );
                            })()}
                            {/* {b.status === "Invoiced" && b.invoice_no && (
                              <button title={`Email Invoice ${b.invoice_no}`} onClick={() => this.setState({ emailModal: b, emailTo: "", emailCc: "" })} className="icon-btn" style={{ color: "#7C3AED", border: "1px solid rgba(124,58,237,0.25)", background: "rgba(124,58,237,0.08)" }}><Mail size={14} /></button>
                            )} */}
                            <button title="Cancel booking" onClick={() => this.handleCancel(b)} disabled={b.status === "Invoiced" || b.status === "Cancelled"} className="icon-btn" style={{ color: "#FCA5A5", border: "1px solid rgba(239,68,68,0.2)", background: "rgba(239,68,68,0.06)" }}><Trash2 size={14} /></button>
                            {/* ── Recalculate — only shown when totals are 0 ── */}
                            {(parseFloat(b.total_sale) === 0 && b.status !== 'Invoiced') && (
                              <button
                                title="Recalculate financials from room data"
                                onClick={async () => {
                                  try {
                                    const { api } = await import('../../utils/api');
                                    await api.patch(`/bookings/${b.id}/recalculate`);
                                    toast.success(`#${b.id} recalculated!`);
                                    this.fetchBookings();
                                  } catch (e) {
                                    toast.error('Recalculate failed');
                                  }
                                }}
                                className="icon-btn"
                                style={{ color: '#0FA968', border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', fontWeight: 700 }}
                                title="Recalculate totals from room data">
                                ₹
                              </button>
                            )}
                            {/* ── Edit Booking ── */}
                            {(() => {
                              const missing = this.getMissingFields(b);
                              return (
                                <button
                                  title="Edit booking"
                                  onClick={() => this.setState({ quickEditModal: b, quickEditData: { ...b } })}
                                  className="icon-btn"
                                  style={{ color: '#C9A84C', border: 'rgba(201,168,76,0.35)', background: 'rgba(201,168,76,0.10)', position: 'relative', cursor: 'pointer' }}>
                                  <Edit2 size={14} />
                                  {missing.length > 0 && (
                                    <span style={{ position: 'absolute', top: -4, right: -4, minWidth: 14, height: 14, borderRadius: 7, background: '#E1483A', border: '2px solid #fff', fontSize: 8, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px' }}>{missing.length}</span>
                                  )}
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ════ PAYMENT MODAL ════ */}
        {this.state.paymentModal && (() => {
          const b = this.state.paymentModal;
          const { paymentTxnId, paymentMode, paymentType, paymentAmount, paymentNotes, paymentSaving } = this.state;
          const PAYMENT_MODES = ["UPI", "NEFT", "RTGS", "IMPS", "Cheque", "Cash", "Credit Card", "Debit Card"];
          const totalSale   = parseFloat(b.total_sale)    || 0;
          const alreadyPaid = parseFloat(b.payment_amount)|| 0;
          const partAmount  = parseFloat(paymentAmount)   || 0;
          const balanceLeft = paymentType === "full" ? 0 : Math.max(0, totalSale - alreadyPaid - partAmount);
          return (
            <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "92vh", boxShadow: "0 24px 64px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ background: "#0B7A4C", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Banknote size={18} color="#6EE7B7" />
                    <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Record Payment</div><div style={{ fontSize: 11, color: "#BEEED9", marginTop: 2 }}>#{b.id} — {b.guest_name || b.hotel_name}</div></div>
                  </div>
                  <button onClick={() => this.setState({ paymentModal: null })} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
                </div>
                <div style={{ padding: "14px 24px", background: "#F0FDF4", borderBottom: "1px solid #D1FAE5", display: "flex", gap: 24 }}>
                  <div><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em" }}>Hotel</div><div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2 }}>{b.hotel_name}</div></div>
                  <div><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em" }}>Sold To</div><div style={{ fontSize: 13, fontWeight: 600, color: "#111827", marginTop: 2 }}>{b.sold_to}</div></div>
                  <div><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em" }}>Invoice</div><div style={{ fontSize: 13, fontWeight: 600, color: "#0FA968", marginTop: 2, fontFamily: "JetBrains Mono,monospace" }}>{b.invoice_no || `₹${fmt(b.total_sale)}`}</div></div>
                </div>
                <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: 16, overflowY: "auto", flex: 1 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Transaction ID *</label>
                    <input type="text" placeholder="e.g. UPI123456789" value={paymentTxnId} onChange={e => this.setState({ paymentTxnId: e.target.value })} autoFocus style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "JetBrains Mono,monospace", fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Mode of Payment *</label>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {PAYMENT_MODES.map(mode => (
                        <button key={mode} type="button" onClick={() => this.setState({ paymentMode: mode })} style={{ padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: paymentMode === mode ? "none" : "1.5px solid #D1D5DB", background: paymentMode === mode ? "#0B7A4C" : "#fff", color: paymentMode === mode ? "#fff" : "#374151" }}>{mode}</button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 8 }}>Payment Type *</label>
                    <div style={{ display: "flex", border: "1.5px solid #D1D5DB", borderRadius: 10, overflow: "hidden" }}>
                      {[["full", "Full Payment"], ["part", "Part Payment"]].map(([type, label]) => (
                        <button key={type} type="button" onClick={() => this.setState({ paymentType: type, paymentAmount: type === "full" ? String(totalSale) : "" })} style={{ flex: 1, padding: "10px 0", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer", background: paymentType === type ? (type === "full" ? "#0B7A4C" : "#1D4ED8") : "#fff", color: paymentType === type ? "#fff" : "#6B7280" }}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <div style={{ flex: 1, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "10px 14px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em", marginBottom: 3 }}>Total Sale</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: "#0B7A4C", fontFamily: "JetBrains Mono,monospace" }}>₹{totalSale.toFixed(2)}</div>
                      {alreadyPaid > 0 && <div style={{ fontSize: 11, color: "#0FA968", marginTop: 3 }}>Already paid: ₹{alreadyPaid.toFixed(2)}</div>}
                    </div>
                    {paymentType === "part" && (
                      <div style={{ flex: 1, background: balanceLeft > 0 ? "#FFF7ED" : "#F0FDF4", border: `1px solid ${balanceLeft > 0 ? "#FED7AA" : "#BBF7D0"}`, borderRadius: 10, padding: "10px 14px" }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#6B7280", letterSpacing: "0.08em", marginBottom: 3 }}>Balance After This</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: balanceLeft > 0 ? "#D97706" : "#0B7A4C", fontFamily: "JetBrains Mono,monospace" }}>₹{balanceLeft.toFixed(2)}</div>
                        {balanceLeft === 0 && <div style={{ fontSize: 11, color: "#0FA968", marginTop: 3 }}>Fully settled ✓</div>}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>{paymentType === "full" ? "Amount (₹) — Full" : "Part Payment Amount (₹) *"}</label>
                    <input type="number" value={paymentType === "full" ? totalSale.toFixed(2) : paymentAmount} readOnly={paymentType === "full"} onChange={e => paymentType === "part" && this.setState({ paymentAmount: e.target.value })} onFocus={e => paymentType === "part" && e.target.select()} style={{ width: "100%", border: `1.5px solid ${paymentType === "part" ? "#2F5FE0" : "#E5E7EB"}`, borderRadius: 10, padding: "10px 14px", fontFamily: "JetBrains Mono,monospace", fontSize: 14, fontWeight: 700, color: paymentType === "full" ? "#0B7A4C" : "#111827", outline: "none", boxSizing: "border-box", background: paymentType === "full" ? "#F0FDF4" : "#fff", cursor: paymentType === "full" ? "not-allowed" : "text" }} />
                    {paymentType === "part" && partAmount > 0 && balanceLeft > 0 && <p style={{ fontSize: 11, color: "#D97706", marginTop: 4 }}>⚠️ Part payment — ₹{balanceLeft.toFixed(2)} will remain outstanding</p>}
                    {paymentType === "part" && partAmount > 0 && balanceLeft === 0 && <p style={{ fontSize: 11, color: "#0FA968", marginTop: 4 }}>✓ This payment will fully settle the invoice</p>}
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>Notes (Optional)</label>
                    <textarea rows={2} placeholder="Any additional payment notes…" value={paymentNotes} onChange={e => this.setState({ paymentNotes: e.target.value })} style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none", resize: "none", boxSizing: "border-box" }} />
                  </div>
                </div>
                <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                  <button type="button" onClick={() => this.setState({ paymentModal: null })} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
                  <button type="button" onClick={this.handlePaymentSave} disabled={paymentSaving || !paymentTxnId.trim()} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: paymentSaving || !paymentTxnId.trim() ? "#9CA3AF" : "#0B7A4C", fontSize: 13, fontWeight: 700, color: "#fff", cursor: paymentSaving || !paymentTxnId.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                    {paymentSaving ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving…</> : <><Banknote size={14} /> Record Payment</>}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ════ HOTEL CONFIRMATION MODAL ════ */}
        {this.state.hotelEmailModal && (() => {
          const b = this.state.hotelEmailModal;
          const { manualConfNo, manualConfirming, manualRejecting, showRejectBox, manualRejectReason } = this.state;
          const closeModal = () => this.setState({ hotelEmailModal: null, hotelEmailTo: "", hotelModalTab: "confirm", manualConfNo: "", showRejectBox: false, manualRejectReason: "" });

          // ── Build room detail rows from room_type_bookings (new) or legacy fields ──
          const rtBookings = b.room_type_bookings || [];
          const roomRows = rtBookings.length > 0 ? (
            rtBookings.map((rt, ri) => {
              const qty     = parseInt(rt.room_count || rt.count) || 0;
              const ebChild = parseInt(rt.extra_bed_child) || 0;
              const ebAdult = parseInt(rt.extra_bed_adult) || 0;
              return (
                <React.Fragment key={ri}>
                  <tr style={{ borderBottom: "1px solid #F3F4F6", background: ri % 2 === 0 ? "#F8FAFF" : "#fff" }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: "#6B7280" }}>Room {ri + 1}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "#16213E" }}>
                      {rt.room_name}
                      <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 400, marginLeft: 6 }}>× {qty} room{qty !== 1 ? "s" : ""}</span>
                    </td>
                  </tr>
                  {(ebChild + ebAdult) > 0 && (
                    <tr style={{ borderBottom: "1px solid #F3F4F6", background: "#FFFBEB" }}>
                      <td style={{ padding: "8px 12px 8px 24px", fontSize: 12, color: "#92400E" }}>🛏️ Extra Bed</td>
                      <td style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600, color: "#92400E" }}>
                        {ebChild > 0 ? `${ebChild} Child (5–12 yrs)` : ""}
                        {ebChild > 0 && ebAdult > 0 ? " · " : ""}
                        {ebAdult > 0 ? `${ebAdult} Adult (12 yrs+)` : ""}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            // Legacy fallback for old bookings
            [["Room Type", b.room_type || "—"], ["Single Rooms", b.single_rooms || "—"], ["Double Rooms", b.double_rooms || "—"], ["Extra Bed", b.extra_bed > 0 ? b.extra_bed : "—"]].map(([label, val]) => (
              <tr key={label} style={{ borderBottom: "1px solid #F3F4F6" }}>
                <td style={{ padding: "10px 12px", fontSize: 13, color: "#6B7280", width: "45%" }}>{label}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "#111827" }}>{val}</td>
              </tr>
            ))
          );

          return (
            <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
              <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 560, boxShadow: "0 24px 64px rgba(0,0,0,0.25)", overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                <div style={{ background: "#16213E", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Hotel size={18} color="#C9A84C" />
                    <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Hotel Confirmation Portal</div><div style={{ fontSize: 11, color: "#B8C8E0", marginTop: 1 }}>#{b.id} — {b.hotel_name}</div></div>
                  </div>
                  <button onClick={closeModal} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
                </div>
                <div style={{ overflowY: "auto", flex: 1 }}>
                  <div>
                    <div style={{ background: "#16213E", padding: "16px 24px" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#C9A84C", marginBottom: 6 }}>Reservation Request</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{b.guest_name || "Guest"}</div>
                      <div style={{ fontSize: 13, color: "#B8C8E0", marginTop: 2 }}>{b.hotel_name}</div>
                    </div>
                    <div style={{ padding: "0 24px 16px" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
                        <tbody>
                          {/* Static rows */}
                          {[["Check In", formatDateLong(b.check_in)], ["Check Out", formatDateLong(b.check_out)], ["Nights", b.total_nights || b.no_of_nights || "—"], ["Sold To", b.sold_to || "—"]].map(([label, val]) => (
                            <tr key={label} style={{ borderBottom: "1px solid #F3F4F6" }}>
                              <td style={{ padding: "10px 12px", fontSize: 13, color: "#6B7280", width: "45%" }}>{label}</td>
                              <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: "#111827" }}>{val}</td>
                            </tr>
                          ))}
                          {/* Dynamic room rows */}
                          {roomRows}
                        </tbody>
                      </table>
                    </div>
                    {!showRejectBox && (
                      <div style={{ padding: "0 24px 20px" }}>
                        <div style={{ background: "#F0F9FF", border: "1.5px solid #BAE6FD", borderRadius: 14, padding: "20px" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#0369A1", marginBottom: 12 }}>✏️ Enter Hotel Confirmation Number</div>
                          <input type="text" placeholder="e.g. TAJ-2024-00123" value={manualConfNo} onChange={e => this.setState({ manualConfNo: e.target.value })} onKeyDown={e => e.key === "Enter" && this.handleManualConfirm()} autoFocus style={{ width: "100%", border: "1.5px solid #BAE6FD", borderRadius: 10, padding: "12px 14px", fontSize: 14, outline: "none", fontFamily: "JetBrains Mono,monospace", color: "#111827", boxSizing: "border-box", marginBottom: 12 }} />
                          <button onClick={this.handleManualConfirm} disabled={manualConfirming || !manualConfNo.trim()} style={{ width: "100%", padding: "12px", borderRadius: 10, border: "none", background: !manualConfNo.trim() || manualConfirming ? "#9CA3AF" : "#16213E", color: "#fff", fontSize: 13, fontWeight: 700, cursor: !manualConfNo.trim() || manualConfirming ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                            {manualConfirming ? <><RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> Confirming…</> : <><Send size={14} /> Save & Confirm Booking</>}
                          </button>
                          <p style={{ fontSize: 11, color: "#6B7280", textAlign: "center", marginTop: 8 }}>Once saved, confirmation emails will be sent to the guest and KTS team automatically.</p>
                        </div>
                        <div style={{ textAlign: "center", marginTop: 16 }}>
                          <button onClick={() => this.setState({ showRejectBox: true })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#E1483A", textDecoration: "underline", fontFamily: "Sora,sans-serif" }}>Unable to accommodate? Reject this booking</button>
                        </div>
                      </div>
                    )}
                    {showRejectBox && (
                      <div style={{ padding: "0 24px 24px" }}>
                        <div style={{ background: "#FCEAE8", border: "1.5px solid #F5CAC5", borderRadius: 14, padding: "20px" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#B5382D", marginBottom: 12 }}>✗ Reject This Booking</div>
                          <textarea rows={3} placeholder="Enter reason for rejection…" value={manualRejectReason} onChange={e => this.setState({ manualRejectReason: e.target.value })} autoFocus style={{ width: "100%", border: "1.5px solid #F5CAC5", borderRadius: 10, padding: "10px 14px", fontSize: 13, outline: "none", fontFamily: "Sora,sans-serif", color: "#111827", resize: "vertical", boxSizing: "border-box", marginBottom: 12 }} />
                          <div style={{ display: "flex", gap: 10 }}>
                            <button onClick={() => this.setState({ showRejectBox: false, manualRejectReason: "" })} style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#374151" }}>Cancel</button>
                            <button onClick={this.handleManualReject} disabled={manualRejecting || !manualRejectReason.trim()} style={{ flex: 2, padding: "10px", borderRadius: 10, border: "none", background: !manualRejectReason.trim() || manualRejecting ? "#9CA3AF" : "#E1483A", color: "#fff", fontSize: 13, fontWeight: 700, cursor: !manualRejectReason.trim() || manualRejecting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                              {manualRejecting ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Rejecting…</> : <><X size={13} /> Confirm Rejection</>}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ padding: "14px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", flexShrink: 0, textAlign: "right" }}>
                  <button onClick={closeModal} style={{ padding: "9px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Close</button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ════ APPROVAL MODAL ════ */}
        {approvalModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 500, boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden" }}>
              <div style={{ background: "#16213E", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <CheckCircle size={18} color="#B7791F" />
                  <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Approve Booking</div><div style={{ fontSize: 11, color: "#B8C8E0", marginTop: 2 }}>#{approvalModal.id} — {approvalModal.guest_name}</div></div>
                </div>
                <button onClick={() => this.setState({ approvalModal: null, approvalReason: "" })} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
              </div>
              <div style={{ padding: "16px 24px", background: "#FFF7ED", borderBottom: "1px solid #FED7AA" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <AlertTriangle size={16} color="#D97706" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400E", marginBottom: 4 }}>Reason submitted by booking team</p>
                    <p style={{ fontSize: 13, color: "#78350F", lineHeight: 1.6, margin: 0 }}>{approvalModal.negative_reason || "— No reason provided —"}</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: "14px 24px", background: "#F8FAFF", borderBottom: "1px solid #E5E7EB", display: "flex", gap: 24 }}>
                {[["Total Sale", fmt(approvalModal.total_sale), "#0FA968"], ["Total Purchase", fmt(approvalModal.total_purchase), "#374151"], ["Gross Profit", fmt(approvalModal.gross_profit), "#E1483A"]].map(([label, val, color]) => (
                  <div key={label}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#9CA3AF", letterSpacing: "0.08em" }}>{label}</div><div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "JetBrains Mono,monospace", marginTop: 2 }}>₹{val}</div></div>
                ))}
              </div>
              <div style={{ padding: "24px" }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 8 }}>Admin Approval Remarks *</label>
                <textarea rows={3} placeholder="Enter your approval remarks…" value={this.state.approvalReason} onChange={e => this.setState({ approvalReason: e.target.value })} style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none", resize: "vertical", boxSizing: "border-box" }} autoFocus />
                <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>Once approved, the booking can be invoiced.</p>
              </div>
              <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => this.setState({ approvalModal: null, approvalReason: "" })} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
                <button type="button" onClick={this.handleApproveSubmit} disabled={approving || !this.state.approvalReason.trim()} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: approving || !this.state.approvalReason.trim() ? "#9CA3AF" : "#0FA968", fontSize: 13, fontWeight: 700, color: "#fff", cursor: approving || !this.state.approvalReason.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  {approving ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Approving…</> : <><CheckCircle size={14} /> Approve Booking</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ REJECTION MODAL ════ */}
        {rejectionModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 500, boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden" }}>
              <div style={{ background: "#7F1D1D", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <X size={18} color="#FCA5A5" />
                  <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Reject Booking</div><div style={{ fontSize: 11, color: "#F5CAC5", marginTop: 2 }}>#{rejectionModal.id} — {rejectionModal.guest_name}</div></div>
                </div>
                <button onClick={() => this.setState({ rejectionModal: null, rejectionReason: "" })} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
              </div>
              <div style={{ padding: "16px 24px", background: "#FFF7ED", borderBottom: "1px solid #FED7AA" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <AlertTriangle size={16} color="#D97706" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#92400E", marginBottom: 4 }}>Reason submitted by booking team</p>
                    <p style={{ fontSize: 13, color: "#78350F", lineHeight: 1.6, margin: 0 }}>{rejectionModal.negative_reason || "— No reason provided —"}</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: "14px 24px", background: "#FCEAE8", borderBottom: "1px solid #F5CAC5", display: "flex", gap: 24 }}>
                {[["Total Sale", fmt(rejectionModal.total_sale), "#0FA968"], ["Total Purchase", fmt(rejectionModal.total_purchase), "#374151"], ["Gross Profit", fmt(rejectionModal.gross_profit), "#E1483A"]].map(([label, val, color]) => (
                  <div key={label}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#9CA3AF", letterSpacing: "0.08em" }}>{label}</div><div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "JetBrains Mono,monospace", marginTop: 2 }}>₹{val}</div></div>
                ))}
              </div>
              <div style={{ padding: "24px" }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 8 }}>Rejection Reason *</label>
                <textarea rows={3} placeholder="Enter reason for rejection…" value={this.state.rejectionReason} onChange={e => this.setState({ rejectionReason: e.target.value })} style={{ width: "100%", border: "1.5px solid #F5CAC5", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none", resize: "vertical", boxSizing: "border-box" }} autoFocus />
                <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>Client will be notified of this rejection via email.</p>
              </div>
              <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => this.setState({ rejectionModal: null, rejectionReason: "" })} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
                <button type="button" onClick={this.handleRejectSubmit} disabled={rejecting || !this.state.rejectionReason.trim()} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: rejecting || !this.state.rejectionReason.trim() ? "#9CA3AF" : "#E1483A", fontSize: 13, fontWeight: 700, color: "#fff", cursor: rejecting || !this.state.rejectionReason.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  {rejecting ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Rejecting…</> : <><X size={14} /> Reject Booking</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ CANCEL BOOKING MODAL ════ */}
        {cancelModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.50)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 500, boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden" }}>
              <div style={{ background: "#7F1D1D", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Trash2 size={18} color="#FCA5A5" />
                  <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Cancel Booking</div><div style={{ fontSize: 11, color: "#F5CAC5", marginTop: 2 }}>#{cancelModal.id} — {cancelModal.guest_name || cancelModal.hotel_name}</div></div>
                </div>
                <button onClick={() => this.setState({ cancelModal: null, cancelReason: "" })} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
              </div>
              <div style={{ padding: "16px 24px", background: "#FFF7ED", borderBottom: "1px solid #FED7AA" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <AlertTriangle size={16} color="#D97706" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 13, color: "#78350F", lineHeight: 1.6, margin: 0 }}>This booking will <strong>not</strong> be deleted. Its status will be set to <strong>Cancelled</strong>, all financial amounts below will be zeroed, and the hotel and client will be emailed with the reason.</p>
                  </div>
                </div>
              </div>
              <div style={{ padding: "14px 24px", background: "#FCEAE8", borderBottom: "1px solid #F5CAC5", display: "flex", gap: 24 }}>
                {[["Total Sale", fmt(cancelModal.total_sale), "#0FA968"], ["Total Purchase", fmt(cancelModal.total_purchase), "#374151"], ["Gross Profit", fmt(cancelModal.gross_profit), "#E1483A"]].map(([label, val, color]) => (
                  <div key={label}><div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "#9CA3AF", letterSpacing: "0.08em" }}>{label}</div><div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: "JetBrains Mono,monospace", marginTop: 2 }}>₹{val} → ₹0.00</div></div>
                ))}
              </div>
              <div style={{ padding: "24px" }}>
                <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 8 }}>Cancellation Reason *</label>
                <textarea rows={3} placeholder="Enter reason for cancellation…" value={this.state.cancelReason} onChange={e => this.setState({ cancelReason: e.target.value })} style={{ width: "100%", border: "1.5px solid #F5CAC5", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none", resize: "vertical", boxSizing: "border-box" }} autoFocus />
                <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>The hotel ({cancelModal.hotel_email_to ? "email on file" : "no email on file"}) and client ({cancelModal.client_email || "no email on file"}) will be notified with this reason.</p>
              </div>
              <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => this.setState({ cancelModal: null, cancelReason: "" })} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Back</button>
                <button type="button" onClick={this.handleCancelSubmit} disabled={cancelling || !this.state.cancelReason.trim()} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: cancelling || !this.state.cancelReason.trim() ? "#9CA3AF" : "#E1483A", fontSize: 13, fontWeight: 700, color: "#fff", cursor: cancelling || !this.state.cancelReason.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  {cancelling ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Cancelling…</> : <><Trash2 size={14} /> Cancel Booking</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ EMAIL INVOICE MODAL ════ */}
        {this.state.emailModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 480, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden" }}>
              <div style={{ background: "#16213E", padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Mail size={18} color="#C9A84C" />
                  <div><div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Email Invoice</div><div style={{ fontSize: 11, color: "#B8C8E0", marginTop: 2 }}>{this.state.emailModal.invoice_no}</div></div>
                </div>
                <button onClick={() => this.setState({ emailModal: null })} style={{ background: "rgba(255,255,255,0.12)", border: "none", borderRadius: 8, padding: 6, cursor: "pointer", color: "#fff", display: "flex" }}><X size={18} /></button>
              </div>
              <div style={{ padding: "16px 24px", background: "#F8FAFF", borderBottom: "1px solid #E5E7EB" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <div><div style={{ fontWeight: 700, color: "#111827" }}>{this.state.emailModal.guest_name}</div><div style={{ color: "#6B7280", marginTop: 2 }}>{this.state.emailModal.hotel_name}</div></div>
                  <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700, color: "#0FA968" }}>₹{new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2 }).format(this.state.emailModal.total_sale)}</div><div style={{ color: "#6B7280", fontSize: 11, marginTop: 2 }}>Total Sale</div></div>
                </div>
              </div>
              <div style={{ padding: "24px" }}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>To (Recipient) *</label>
                  <input type="email" placeholder="agent@example.com" value={this.state.emailTo} onChange={e => this.setState({ emailTo: e.target.value })} style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none" }} autoFocus />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#6B7280", display: "block", marginBottom: 6 }}>CC (Optional)</label>
                  <input type="email" placeholder="manager@example.com" value={this.state.emailCc} onChange={e => this.setState({ emailCc: e.target.value })} style={{ width: "100%", border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "10px 14px", fontFamily: "Sora,sans-serif", fontSize: 13, color: "#111827", outline: "none" }} />
                </div>
                <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 8 }}>The invoice PDF ({this.state.emailModal.invoice_no}.pdf) will be attached automatically.</p>
              </div>
              <div style={{ padding: "16px 24px", background: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                <button type="button" onClick={() => this.setState({ emailModal: null })} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #D1D5DB", background: "#fff", fontSize: 13, fontWeight: 600, color: "#374151", cursor: "pointer" }}>Cancel</button>
                <button type="button" onClick={this.handleSendEmail} disabled={this.state.emailSending} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: this.state.emailSending ? "#9CA3AF" : "#16213E", fontSize: 13, fontWeight: 700, color: "#fff", cursor: this.state.emailSending ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                  {this.state.emailSending ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Sending…</> : <><Send size={14} /> Send Invoice</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ EDIT BOOKING MODAL ════ */}
        {this.state.quickEditModal && (() => {
          const b = this.state.quickEditModal;
          const { quickEditData: d, quickEditSaving } = this.state;
          const set = (k, v) => this.setState(prev => ({ quickEditData: { ...prev.quickEditData, [k]: v } }));

          const iStyle = (focus) => ({
            width: '100%', height: 38, border: '1.5px solid #D1D5DB', borderRadius: 8,
            padding: '0 10px', fontSize: 13, fontFamily: 'Sora,sans-serif',
            outline: 'none', boxSizing: 'border-box', color: '#111827', background: '#fff',
          });
          const lbl = (text) => (
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{text}</div>
          );
          const inp = (key, type = 'text', placeholder = '') => (
            <input type={type} value={d[key] ?? ''} placeholder={placeholder}
              onChange={e => set(key, e.target.value)}
              onFocus={e => { e.target.style.borderColor = '#C9A84C'; }}
              onBlur={e => { e.target.style.borderColor = '#D1D5DB'; }}
              style={iStyle()} />
          );
          const numInp = (key, placeholder = '0') => (
            <input type="number" min="0" step="0.01" value={d[key] ?? ''} placeholder={placeholder}
              onChange={e => set(key, e.target.value)}
              onFocus={e => { e.target.select(); e.target.style.borderColor = '#C9A84C'; }}
              onBlur={e => { e.target.style.borderColor = '#D1D5DB'; }}
              style={iStyle()} />
          );
          const section = (title, color, children) => (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.1em', borderBottom: `2px solid ${color}22`, paddingBottom: 6, marginBottom: 12 }}>{title}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>
            </div>
          );
          const full = (children) => <div style={{ gridColumn: '1 / -1' }}>{children}</div>;

          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
              <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 640, maxHeight: '94vh', boxShadow: '0 24px 64px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ background: '#16213E', padding: '16px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(201,168,76,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Edit2 size={16} color="#C9A84C" />
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Edit Booking #{b.id}</div>
                      <div style={{ fontSize: 11, color: '#B8C8E0', marginTop: 1 }}>{b.hotel_name} · {b.guest_name || 'No guest name'}</div>
                    </div>
                  </div>
                  <button onClick={() => this.setState({ quickEditModal: null, quickEditData: {} })}
                    style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#fff', display: 'flex' }}>
                    <X size={18} />
                  </button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px 22px', overflowY: 'auto', flex: 1 }}>

                  {section('👤 Guest Information', '#1D4ED8',
                    <>
                      <div>{lbl('Guest Name')}{inp('guest_name', 'text', 'e.g. John Doe')}</div>
                      <div>{lbl('Guest Phone')}{inp('guest_phone', 'tel', 'e.g. 9876543210')}</div>
                      <div>{lbl('Client Email')}{inp('client_email', 'email', 'client@email.com')}</div>
                      <div>{lbl('Sold To (Agency)')}{inp('sold_to', 'text', 'Agency name')}</div>
                      <div>{lbl('Billing State')}{inp('billing_state_sale', 'text', 'e.g. Maharashtra')}</div>
                    </>
                  )}

                  {section('🏨 Hotel & Stay', '#0FA968',
                    <>
                      <div>{lbl('Hotel Name')}{inp('hotel_name', 'text')}</div>
                      <div>{lbl('Destination')}{inp('destination', 'text')}</div>
                      <div>{lbl('Check In')}{inp('check_in', 'date')}</div>
                      <div>{lbl('Check Out')}{inp('check_out', 'date')}</div>
                      <div>{lbl('No. of Nights')}{numInp('no_of_nights')}</div>
                      <div>{lbl('Conf. Number')}{inp('conf_no', 'text', 'Hotel confirmation no.')}</div>
                    </>
                  )}

                  {section('📋 Sale Details', '#7C3AED',
                    <>
                      <div>{lbl('Sale Type')}{inp('sale_type', 'text', 'e.g. Hotel')}</div>
                      <div>{lbl('Booking Date')}{inp('booking_date', 'date')}</div>
                      <div>{lbl('Booked By')}{inp('booked_by', 'text')}</div>
                      <div>{lbl('Rate Mode')}{
                        <select value={d['mode'] ?? ''} onChange={e => set('mode', e.target.value)}
                          style={{ ...iStyle(), cursor: 'pointer' }}>
                          <option value="">-- Select --</option>
                          <option value="Full Input">Full Input</option>
                          <option value="Service Charge">Service Charge</option>
                        </select>
                      }</div>
                      <div>{lbl('Purchase From')}{inp('purchase_from', 'text')}</div>
                      <div>{lbl('Billing State (Purchase)')}{inp('billing_state_purchase', 'text')}</div>
                    </>
                  )}

                  {section('💰 Financials', '#D97706',
                    <>
                      <div>{lbl('Total Sale (₹)')}{numInp('total_sale')}</div>
                      <div>{lbl('Total Purchase (₹)')}{numInp('total_purchase')}</div>
                      <div>{lbl('Extras Amount Sale (₹)')}{numInp('extras_amount_sale')}</div>
                      <div>{lbl('Extras Amount Purchase (₹)')}{numInp('extras_amount_purchase')}</div>
                      {full(<div>{lbl('Extras Details (Sale)')}{inp('extras_details_sale', 'text', 'Describe extras...')}</div>)}
                      {full(<div>{lbl('Extras Details (Purchase)')}{inp('extras_details_purchase', 'text', 'Describe extras...')}</div>)}
                    </>
                  )}

                  {section('📧 Hotel Email', '#0369A1',
                    <>
                      <div>{lbl('Hotel Email To')}{inp('hotel_email_to', 'email')}</div>
                      <div>{lbl('Hotel Email CC')}{inp('hotel_email_cc', 'email')}</div>
                    </>
                  )}

                  {section('📝 Notes', '#6B7280',
                    <>
                      {full(<div>{lbl('Negative Reason (if any)')}<textarea value={d['negative_reason'] ?? ''} onChange={e => set('negative_reason', e.target.value)} rows={2}
                        style={{ width: '100%', border: '1.5px solid #D1D5DB', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'Sora,sans-serif', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                        onFocus={e => { e.target.style.borderColor = '#C9A84C'; }}
                        onBlur={e => { e.target.style.borderColor = '#D1D5DB'; }} /></div>)}
                    </>
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding: '14px 22px', background: '#F9FAFB', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>Room types are managed in the full booking form</span>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="button" onClick={() => this.setState({ quickEditModal: null, quickEditData: {} })}
                      style={{ padding: '10px 20px', borderRadius: 10, border: '1.5px solid #D1D5DB', background: '#fff', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button type="button" onClick={this.handleQuickEditSave} disabled={quickEditSaving}
                      style={{ padding: '10px 24px', borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700, color: '#fff', cursor: quickEditSaving ? 'not-allowed' : 'pointer', background: quickEditSaving ? '#9CA3AF' : '#16213E', display: 'flex', alignItems: 'center', gap: 8 }}>
                      {quickEditSaving
                        ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                        : <><Send size={13} /> Update Booking</>}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        <style>{`
          @keyframes spin  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        `}</style>
      </div>
    );
  }
}

export default BookingsList;